import { AfterViewInit, Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[questionMarkMask]',
    standalone: true,
})
export class QuestionMarkMaskDirective implements AfterViewInit {
	/// <summary>
	/// The mask to apply to the input field using the question mark mask directive. (this is specifically for ? masking)
	/// </summary>
	@Input() questionMarkMask: string;

	/// <summary>
	/// If set to true, only the user input will follow the mask structure but will not impose ? for missing data.
	/// Otherwise, all characters will be masked and missing data will be replaced with ?. Default is false.
	/// </summary>
	@Input() questionMarkMaskUserInputOnly = false;

	questionMarkMaskHelper: QuestionMarkMaskHelper;

	constructor(private el: ElementRef, private control: NgControl, private renderer: Renderer2) {}

	ngAfterViewInit(): void {
		this.questionMarkMaskHelper = new QuestionMarkMaskHelper(this.questionMarkMask || '', this.questionMarkMaskUserInputOnly || false);
	}

	@HostListener('input', ['$event'])
	onInputChange(event: InputEvent) {
		if (!this.questionMarkMaskHelper.isActive) {
			return;
		}

		let formattedValue = this.questionMarkMaskHelper.getFormattedValue(this.el.nativeElement.value);

		if (formattedValue !== this.el.nativeElement.value) {
			// Regular expression to match only letters, numbers, Question Marks, and periods
			const regex = /^[a-zA-Z0-9?.]*$/;

			// Check each character against the regex
			if (!regex.test(formattedValue)) {
				// Remove any character that is not a letter or number
				formattedValue = formattedValue.replace(/[^a-zA-Z0-9?.]/g, '');
			}

			event.preventDefault();

			this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
			// If using FormControl, update its value as well
			if (this.control && this.control.control) {
				this.control.control.setValue(formattedValue, { emitEvent: false });
			}
		}

		this.el.nativeElement.setSelectionRange(this.questionMarkMaskHelper.selectionStart, this.questionMarkMaskHelper.selectionEnd);
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		this.questionMarkMaskHelper.setEvent(event, this.el);
	}

	// Listen for paste events
	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent) {
		this.questionMarkMaskHelper.setEvent(event, this.el);
	}
}

class QuestionMarkMaskHelper {
	private questionMarkMask = '';
	private questionMarkMaskUserInputOnly = false;
	private valueBeforeEvent = '';
	private origValue = '';
	private cleanedValue = '';
	private cursorPosition = 0;
	private newCursorPosition = 0;
	private lastEvent: KeyboardEvent | ClipboardEvent;
	private formattedValue = '';

	constructor(questionMarkMask: string, questionMarkMaskUserInputOnly: boolean) {
		this.questionMarkMask = questionMarkMask;
		this.questionMarkMaskUserInputOnly = questionMarkMaskUserInputOnly;
	}

	public setEvent(event: KeyboardEvent | ClipboardEvent, el: ElementRef): void {
		if (!this.isActive) {
			return;
		}

		this.lastEvent = event;
		this.valueBeforeEvent = el?.nativeElement?.value;
		this.cursorPosition = el?.nativeElement?.selectionStart;
		this.newCursorPosition = el?.nativeElement?.selectionEnd;

		if (this.lastEvent instanceof KeyboardEvent) {
			const userKeys = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft', 'Enter', 'Escape', 'Tab', 'Shift', 'Alt', 'Backspace', 'Delete'];
			if (!userKeys.includes(this.lastEvent.key) && this.isSingleCharSelected()) {
				// This is needed to get around maxLength restrictions.  maxLength prevents the input from being changed if the cursor has zero chars selected.
				el.nativeElement.setSelectionRange(this.cursorPosition, this.cursorPosition + 1);
			}
		}
	}

	public getFormattedValue(value: string): string {
		if (!this.isActive) {
			return;
		}

		this.origValue = value;
		this.cleanedValue = this.origValue.replace(/\./g, '');

		if (this.lastEvent instanceof KeyboardEvent) {
			if (this.lastEvent.key === 'Backspace') {
				return this.handleBackspace();
			}

			if (this.lastEvent.key === 'Delete') {
				return this.handleDelete();
			}
		}

		if (!this.isLastEventKeyValid()) {
			this.handleInvalidKey();
		}

		this.buildFormattedValue();

		if (this.isSingleCharSelected()) {
			this.handleSingleCharSelected();
		} else if (this.isMultipleCharsSelected()) {
			this.handleMultipleCharsSelected();
		}

		return this.finishedFormattedValue;
	}

	private buildFormattedValue(): void {
		this.formattedValue = '';
		const chars = this.questionMarkMask.split('');
		for (const char of chars) {
			if (this.questionMarkMaskUserInputOnly) {
				if (char === '.') {
					this.formattedValue += '.';
				} else {
					this.formattedValue += this.cleanedValue.charAt(0).length > 0 ? this.cleanedValue.charAt(0) : '';
					this.cleanedValue = this.cleanedValue.substring(1);
				}

				if (this.cleanedValue.length === 0) {
					if (this.questionMarkMask.charAt(this.formattedValue.length) === '.') {
						if (this.lastEvent instanceof KeyboardEvent && this.lastEvent.key !== 'Backspace') {
							this.formattedValue += '.';
						}
					}
					break;
				}
			} else {
				if (char === '.') {
					this.formattedValue += '.';
				} else {
					this.formattedValue += this.cleanedValue.charAt(0).length > 0 ? this.cleanedValue.charAt(0) : '?';
					this.cleanedValue = this.cleanedValue.substring(1);
				}
			}
		}
	}

	private isLastEventKeyValid(): boolean {
		const e = this.lastEvent;
		const regex = /^[a-zA-Z0-9?]*$/;
		if (e instanceof KeyboardEvent && !regex.test(e.key)) {
			return false;
		}

		return true;
	}

	private handleInvalidKey(): void {
		this.cleanedValue = this.valueBeforeEvent.replace(/\./g, '');
	}

	private isSingleCharSelected(): boolean {
		return this.cursorPosition === this.newCursorPosition;
	}

	private handleSingleCharSelected(): void {
		// handle case where cursor is at the beginning of the input
		if (this.lastEvent instanceof ClipboardEvent) {
			return;
		}

		const curChar = this.formattedValue.charAt(this.cursorPosition);
		const afterCursor = this.formattedValue.substring(this.cursorPosition + 1);
		const charKey = this.lastEvent instanceof KeyboardEvent ? this.lastEvent.key : '';
		this.newCursorPosition = this.cursorPosition + 1;

		if (curChar === '.' && charKey !== '.') {
			this.newCursorPosition++;
		} else if (afterCursor.charAt(0) === '.') {
			this.newCursorPosition++;
		}
	}

	private isMultipleCharsSelected(): boolean {
		return this.cursorPosition !== this.newCursorPosition;
	}

	private handleMultipleCharsSelected(): void {
		const charsSelected = this.newCursorPosition - this.cursorPosition;
		let afterCursor = '';

		if (charsSelected > 1 && this.lastEvent instanceof KeyboardEvent) {
			// multiple characters selected

			const beforeCursor = this.valueBeforeEvent.substring(0, this.cursorPosition);
			afterCursor = this.valueBeforeEvent.substring(this.newCursorPosition);
			const defaultChar = this.questionMarkMaskUserInputOnly ? '' : '?';
			const defaults = this.valueBeforeEvent
				.substring(this.cursorPosition + 1, this.newCursorPosition)
				.replace(/[a-zA-Z0-9]/g, `${defaultChar}`);
			const newData = this.lastEvent.key + defaults;
			this.cleanedValue = (beforeCursor + newData + afterCursor).replace(/\./g, '');

			this.buildFormattedValue();
			this.newCursorPosition = this.cursorPosition + 1;
		}

		afterCursor = this.formattedValue.substring(this.newCursorPosition);
		if (afterCursor.charAt(0) === '.') {
			this.newCursorPosition++;
		}
	}

	private handleBackspace(): string {
		// handle case where backspace is pressed
		const charsSelected = this.newCursorPosition - this.cursorPosition;

		if (charsSelected === 0) {
			if (!this.questionMarkMaskUserInputOnly) {
				this.cleanedValue = this.valueBeforeEvent.replace(/\./g, '');
				this.buildFormattedValue();
				const beforeCursor = this.formattedValue.substring(0, this.cursorPosition - 1);
				const removedChar = this.formattedValue.charAt(this.cursorPosition - 1);
				const afterCursor = this.formattedValue.substring(this.cursorPosition);
				this.formattedValue = beforeCursor + `${removedChar === '.' ? '.' : '?'}` + afterCursor;
			} else {
				this.buildFormattedValue();
			}
			this.newCursorPosition--;
		} else {
			// more than 1 character selected
			this.buildFormattedValue();
			this.newCursorPosition = this.cursorPosition;
		}

		return this.finishedFormattedValue;
	}

	private handleDelete(): string {
		// handle case where delete is pressed

		this.buildFormattedValue();
		this.newCursorPosition = this.cursorPosition;

		return this.finishedFormattedValue;
	}

	public get isActive(): boolean {
		return this.questionMarkMask?.length > 0;
	}

	public get selectionStart(): number {
		return this.newCursorPosition;
	}

	public get selectionEnd(): number {
		if (this.lastEvent instanceof KeyboardEvent) {
			if (this.lastEvent.key === 'Backspace' || this.lastEvent.key === 'Delete') {
				return this.newCursorPosition;
			}
		}

		return this.newCursorPosition + 1;
	}

	private get finishedFormattedValue(): string {
		return this.formattedValue.toUpperCase();
	}
}
